import type { ReactNode } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SwipeableDrawer,
  Typography
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";

// ---- Types ----------------------------------------------------------------

export interface ItemAction {
  /** Stable id for React keys. */
  id: string;
  /** Main label, e.g. "Add money". */
  label: string;
  /** Optional helper text shown below the label. */
  description?: string;
  /** Left-side icon element. */
  icon?: ReactNode;
  /** Called when the row is tapped. The sheet is closed automatically. */
  onSelect: () => void;
  /** Render with destructive (error) color styling. */
  destructive?: boolean;
  /** Disable the row. */
  disabled?: boolean;
}

export interface ItemActionSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional open handler for SwipeableDrawer (keeps iOS swipe-to-open off). */
  onOpen?: () => void;
  /** Header title, e.g. "SPY · Long". */
  title: string;
  /** Optional subtitle below the title. */
  subtitle?: string;
  actions: ItemAction[];
}

// ---- Component ------------------------------------------------------------

/**
 * Bottom-sheet action menu for list items. Renders a SwipeableDrawer anchored
 * at the bottom with a grab handle, title/subtitle, a list of actions, and a
 * Cancel button. Selecting any action closes the sheet.
 *
 * Designed for iOS-like feel — rounded top corners, dimmer backdrop, drag to
 * dismiss.
 */
export function ItemActionSheet({
  open,
  onClose,
  onOpen,
  title,
  subtitle,
  actions
}: ItemActionSheetProps) {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen ?? (() => {})}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          ...desktopBottomDrawerPaperSx,
        }
      }}
    >
      {/* Grab handle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: 1,
          pb: 0.5
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: (t: Theme) => t.palette.divider
          }}
        />
      </Box>

      {/* Header */}
      <Stack sx={{ px: 2, pt: 1, pb: 1 }} spacing={0.25}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {subtitle}
          </Typography>
        )}
      </Stack>

      <Divider />

      {/* Actions */}
      <List sx={{ py: 0.5 }}>
        {actions.map((a) => (
          <ListItem key={a.id} disablePadding>
            <ListItemButton
              onClick={() => {
                a.onSelect();
                onClose();
              }}
              disabled={a.disabled}
              sx={{
                px: 2,
                py: 1.25,
                color: a.destructive ? "error.main" : "text.primary",
                "& .MuiListItemIcon-root": {
                  color: a.destructive ? "error.main" : "text.secondary",
                  minWidth: 36
                }
              }}
            >
              {a.icon !== undefined && <ListItemIcon>{a.icon}</ListItemIcon>}
              <ListItemText
                primary={a.label}
                secondary={a.description}
                primaryTypographyProps={{ fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: 12 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Cancel */}
      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          onClick={onClose}
          sx={{ fontWeight: 500, py: 1 }}
        >
          Cancel
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}
